import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeHolidayDayStatus,
  createHolidayDay,
  deleteHolidayDay,
  getHolidayDays,
  updateHolidayDay,
} from '../api/holidayDayApi'
import type { HolidayDay, HolidayDayPage, HolidayDayStatus, HolidayDayUpsert } from '../types'

const KEY = 'holiday-days'

/**
 * Tatil Günü yönetimi sunucu-durumu: liste + oluştur/güncelle/durum/sil. Her başarıda liste
 * invalidate edilir; güncel kayıt ayrıca cache'e yazılır (bayat `modDate` → sahte 409 önlenir).
 */
export function useHolidayDays(q?: string) {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
  }

  const applyUpdated = (updated: HolidayDay) =>
    qc.setQueryData<HolidayDayPage>([KEY, i18n.language, q ?? ''], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((h) => (h.id === updated.id ? updated : h)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language, q ?? ''],
    queryFn: () => getHolidayDays(q),
  })

  // meta.handledCodes: aynı tarih çakışması ekranda ALAN hatası olarak gösterilir → merkezî toast susar.
  const create = useMutation({
    mutationFn: (body: HolidayDayUpsert) => createHolidayDay(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: HolidayDayUpsert }) => updateHolidayDay(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: HolidayDayStatus }) =>
      changeHolidayDayStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteHolidayDay(id),
    onSuccess: invalidate,
  })

  return { list, create, update, changeStatus, remove }
}
